import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { apiRequest } from "../services/api";
import logo from "../assets/logo.png";

export default function Footer() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      try {
        const response = await apiRequest("/storefront/categories");

        if (mounted) {
          setCategories(response || []);
        }
      } catch {
        // Footer must never break if public categories cannot load.
        if (mounted) {
          setCategories([]);
        }
      } finally {
        if (mounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const quickLinks = [
    ["Home", "/"],
    ["New Arrivals", "/search/all?sort=newest"],
    ["Shop All", "/search/all"],
    ["Shopping Bag", "/cart"],
  ];

  const categoryLinks = categories.slice(0, 6).map((category) => [
    category.name,
    `/category/${category.slug || category.id}`,
  ]);

  const careLinks = [
    ["About CHARMS", "/aboutus"],
    ["Contact Us", "/contact"],
    ["Shipping Policy", "/shippingpolicy"],
    ["Refund Policy", "/refundpolicy"],
    ["Privacy Policy", "/privacypolicy"],
    ["Delete customer account", "/account-deletion"],
    ["HRMS Privacy", "/hrms-privacy"],
    ["Terms & Conditions", "/termsandconditions"],
    ["Loyalty Programe Terms", "/loyaltyterms"],
  ];

  const goTo = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-slate-950 text-white">
      <section className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-10 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-200">
              CHARMS Rewards
            </p>

            <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
              Make every purchase more rewarding.
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/60">
              Create your account, earn loyalty points and enjoy a more
              personal CHARMS shopping experience.
            </p>
          </div>

          <button
            type="button"
            onClick={() => goTo("/signup")}
            className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-slate-950 transition hover:bg-primary-700 hover:text-white"
          >
            Create Account
            <ArrowUpRight size={16} />
          </button>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => goTo("/")}
              className="flex items-center gap-3"
              aria-label="Go to CHARMS home page"
            >
              <img src={logo} alt="" className="h-10 w-10 object-contain" />

              <span className="text-2xl font-semibold tracking-[0.22em]">
                CHARMS
              </span>
            </button>

            <p className="mt-6 max-w-sm text-sm leading-7 text-white/60">
              Premium fashion and lifestyle collections from the brands you
              love, carefully curated for every occasion.
            </p>

            <div className="mt-7 inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-semibold text-white/75">
              <ShieldCheck size={15} className="text-primary-200" />
              Authentic brands. Trusted shopping.
            </div>
          </div>

          <FooterLinks title="Shop" links={quickLinks} onNavigate={goTo} />

          <FooterLinks
            title="Categories"
            links={categoryLinks}
            onNavigate={goTo}
            isLoading={isLoadingCategories}
            emptyText="Categories are coming soon."
          />

          <FooterLinks
            title="Customer Care"
            links={careLinks}
            onNavigate={goTo}
          />

          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-white">
              Contact
            </h3>

            <div className="mt-6 space-y-5 text-sm text-white/60">
              <a
                href="tel:+919876543210"
                className="group flex items-start gap-3 transition hover:text-white"
              >
                <Phone
                  size={16}
                  className="mt-0.5 shrink-0 text-primary-200 transition group-hover:text-white"
                />
                <span>(+91) 98765-43210</span>
              </a>

              <a
                href="mailto:info@charmscollections.com"
                className="group flex items-start gap-3 break-all transition hover:text-white"
              >
                <Mail
                  size={16}
                  className="mt-0.5 shrink-0 text-primary-200 transition group-hover:text-white"
                />
                <span>info@charmscollections.com</span>
              </a>

              <div className="flex items-start gap-3">
                <MapPin
                  size={16}
                  className="mt-0.5 shrink-0 text-primary-200"
                />
                <span>Punjab, India</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} CHARMS. All rights reserved.</p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <button
              type="button"
              onClick={() => goTo("/privacypolicy")}
              className="transition hover:text-white"
            >
              Privacy
            </button>

            <button
              type="button"
              onClick={() => goTo("/termsandconditions")}
              className="transition hover:text-white"
            >
              Terms
            </button>

            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={13} />
              Secure payments · Easy returns
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLinks({
  title,
  links,
  onNavigate,
  isLoading = false,
  emptyText,
}) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-white">
        {title}
      </h3>

      <div className="mt-6 space-y-3">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-3 w-24 animate-pulse bg-white/10"
              />
            ))}
          </>
        ) : links.length > 0 ? (
          links.map(([label, path]) => (
            <button
              key={`${label}-${path}`}
              type="button"
              onClick={() => onNavigate(path)}
              className="group flex w-fit items-center gap-1 text-left text-sm text-white/60 transition hover:text-white"
            >
              <span>{label}</span>

              <ArrowUpRight
                size={13}
                className="opacity-0 transition duration-200 group-hover:opacity-100"
              />
            </button>
          ))
        ) : (
          <p className="text-xs leading-5 text-white/35">{emptyText}</p>
        )}
      </div>
    </div>
  );
}
